'use client';

/**
 * Who the person at the keyboard says they are — asked once, then shared.
 *
 * WHY THIS EXISTS AT ALL, given that `fetchIdentity` is one line. Because there
 * are now THREE things on the page that need the same answer, and they need it
 * to be the same answer:
 *
 *   • IdentityGate — the mandatory wall, which opens when the server says this
 *     visitor is still going by a generated name;
 *   • the settings gear in the header, which animates for exactly the people
 *     that wall is for, and must stop the instant one of them answers;
 *   • the Profile tab behind that gear, which both reads the current answer and
 *     writes a new one.
 *
 * Three components each calling `/api/identity` would be three round trips and,
 * worse, three copies of a fact that changes: answer the wall and the gear would
 * carry on waving until a reload, because nothing told it. One provider, one
 * request, one `save()` that every reader sees the result of.
 *
 * WHAT IT IS NOT. Not auth, not a session, not a login — see lib/identity.ts on
 * the server for the whole of that argument. It is a label the user chooses for
 * themselves so the console stops being a list of strangers.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import { fetchIdentity, claimIdentity, avatarSrc, type IdentityState } from './api';
import { DEFAULT_COMPANY } from './company';
import { useAuth } from './auth';

/**
 * "I am not on a team" — the one team number that changes what the form does.
 *
 * Picking it stops the dialog asking for a nickname: the box is filled with the
 * one fixed name team 0 is saved under and turned read-only. Every other number
 * leaves the box exactly as it was.
 *
 * Mirrors NO_TEAM in the backend's lib/identity.ts, and both dialogs read it
 * from here rather than writing a bare `0` — the rule exists in two places on
 * screen (the wall and the Profile tab) and must be one rule.
 */
export const NO_TEAM = 0;

/**
 * What the nickname box should show, and whether it can be typed in.
 *
 * The shared half of the team-0 rule. Note what it does NOT do: it never
 * touches the caller's own `nickname` state. That is the whole trick behind
 * "leaving other team numbers unchanged" — somebody half-way through typing
 * "Jin Ho" who tries team 0 and changes their mind finds "Jin Ho" still there,
 * because it was never overwritten, only covered.
 *
 * `noTeamNickname` is null only if the server did not send one — an older
 * backend, or a failed lookup. The box then falls back to whatever was typed
 * rather than locking somebody out of a field showing nothing, and it costs
 * nothing to be wrong about, because the server writes its own copy on the way
 * in and ignores what the request carried.
 */
export function nicknameField(
  team: number | null,
  typed: string,
  noTeamNickname: string | null,
): { value: string; locked: boolean } {
  if (team !== NO_TEAM) return { value: typed, locked: false };
  return { value: noTeamNickname ?? typed, locked: !!noTeamNickname };
}

/** What they answered, so a second ask arrives pre-filled rather than blank. */
const STORE = 'gt_identity';

function remember(nickname: string, team: number, company: string): void {
  try {
    localStorage.setItem(STORE, JSON.stringify({ nickname, team, company }));
  } catch {
    /* private window, or storage disabled — the server has it, which is the copy
       that counts. */
  }
}

function recall(): { nickname: string; team: number | null; company: string } {
  try {
    const raw = localStorage.getItem(STORE);
    if (raw) {
      const v = JSON.parse(raw);
      return {
        nickname: typeof v?.nickname === 'string' ? v.nickname : '',
        team: typeof v?.team === 'number' ? v.team : null,
        // '' rather than the default company: this is what THEY answered, and
        // an entry written before the box existed answered nothing. The
        // default belongs to the form, which fills an empty box with it.
        company: typeof v?.company === 'string' ? v.company : '',
      };
    }
  } catch {
    /* unreadable or not JSON — treat it as absent. */
  }
  return { nickname: '', team: null, company: '' };
}

export interface Identity {
  /**
   * The server has answered, one way or the other.
   *
   * False covers both "still asking" and "no provider above me", and both of
   * those mean the same thing to a reader: do not draw anything yet. A gear that
   * appeared and then had to admit it knew nothing would be worse than a gear
   * that arrived a beat late.
   */
  loaded: boolean;
  /** null when the lookup failed — which is not the same as "no nickname". */
  state: IdentityState | null;
  /** Their nickname, or '' — from the server, falling back to this browser's copy. */
  nickname: string;
  team: number | null;
  /** Who they work for. With the team, this is the label — `Shark-2`. */
  company: string;
  /** The teams that exist. From the server, so the buttons cannot drift from it. */
  teams: number[];
  maxNickname: number;
  maxCompany: number;
  /** What an empty company box is filled with. From the server. */
  defaultCompany: string;
  /** The one nickname team 0 is saved under — what it locks the box to. */
  noTeamNickname: string | null;
  /** Their picture (the account's Tiger or uploaded photo), as a URL, or null if they have none. Live across Tiger saves. */
  avatarUrl: string | null;
  /** Remember a new one (or its removal) without re-asking the server. */
  setAvatarUrl: (url: string | null) => void;
  /**
   * The server holds no nickname for this person — so the gear asks for one.
   *
   * DELIBERATELY NOT `needsName`, and the difference is the whole feature. That
   * flag is the WALL's trigger, and it is true only while the wall is up — at
   * which point the gear is behind a backdrop and hidden by its own rule. Tying
   * the cue to it would have produced an animation that could not fire in any
   * state the app can reach: walled users cannot see it, and everyone who gets
   * past the wall has a nickname by definition.
   *
   * So it asks the simpler question instead, and what it catches is everybody
   * the wall does not: visitors an operator has named by hand, and — the case
   * that matters — every visitor at all when `identityPromptEnabled` is off. A
   * gear that bobs is the polite alternative to a dialog with no Cancel, and an
   * operator who switches the wall off should get that rather than nothing.
   *
   * `unavailable` is the one exclusion, and it is not fussiness: a failed lookup
   * arrives as an empty nickname exactly like a real one, and nagging somebody
   * over a fact we could not check is the one thing a cue like this must not do.
   */
  unnamed: boolean;
  /** The mandatory wall should be on screen. */
  gateOpen: boolean;
  /** Store an answer. Throws with the server's own words, like `claimIdentity`. */
  save: (nickname: string, company: string, team: number) => Promise<void>;
}

const FALLBACK: Identity = {
  loaded: false,
  state: null,
  nickname: '',
  team: null,
  company: '',
  teams: [0, 1, 2, 3, 4],
  maxNickname: 40,
  maxCompany: 24,
  defaultCompany: DEFAULT_COMPANY,
  noTeamNickname: null,
  avatarUrl: null,
  setAvatarUrl: () => {},
  unnamed: false,
  gateOpen: false,
  save: async () => {
    throw new Error('Identity is not available on this page.');
  },
};

const Ctx = createContext<Identity>(FALLBACK);

export function useIdentity(): Identity {
  return useContext(Ctx);
}

/**
 * Mounted on the translator page, NOT in the root layout.
 *
 * The same reason IdentityGate is mounted there and app/layout.tsx says so at
 * length: that layout also wraps the admin console, and the console must never
 * grow a wall, a gear or a nag about a nickname. Nothing here reaches it,
 * because nothing here is above it.
 */
export function IdentityProvider({ children }: { children: ReactNode }) {
  /*
   * The account, when there is one — and there now always is one on the
   * translator, because AuthGate is above this provider.
   *
   * This is where identity stopped being a property of the network. `nickname`
   * and `team` below prefer what the signed-in person's ACCOUNT says over
   * anything the /api/identity lookup returned, so the profile dialog fills
   * itself in from the account, and two people behind one VPN exit no longer
   * read each other's answers back out of a shared visitor document.
   */
  const auth = useAuth();
  const [state, setState] = useState<IdentityState | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [answer, setAnswer] = useState<{
    nickname: string; team: number | null; company: string;
  }>({ nickname: '', team: null, company: '' });
  const [gateOpen, setGateOpen] = useState(false);
  /**
   * Held here rather than in the dialog, so the picture survives the dialog
   * being closed and reopened — and so anything else that ever wants to show it
   * reads the same value.
   */
  // `undefined` = nobody has told us yet, so the account's own key (from
  // /api/auth/me) is drawn meanwhile; an explicit null still means "no picture".
  const [avatarUrl, setAvatarUrl] = useState<string | null | undefined>(undefined);
  // Signed out, or another account signed in, without a reload: the picture
  // held above was the last account's, so go back to whatever this one says.
  const who = auth.user?.id ?? null;
  const [avatarFor, setAvatarFor] = useState<string | null>(who);
  if (avatarFor !== who) {
    if (avatarFor !== null) setAvatarUrl(undefined);
    setAvatarFor(who);
  }

  /*
   * The Tiger editor announces every save on `window` (lib/tiger.ts
   * announceAvatar). Listened for by name rather than imported, so the tiger
   * runtime stays out of this bundle. The header, the account menu and the
   * Settings photo all read `avatarUrl`, so they redraw without a reload.
   */
  useEffect(() => {
    const onAvatar = (e: Event) => setAvatarUrl(avatarSrc((e as CustomEvent).detail?.avatarKey));
    window.addEventListener('kharma:avatar', onAvatar);
    return () => window.removeEventListener('kharma:avatar', onAvatar);
  }, []);

  // Asked after mount rather than server-rendered: the markup the server sends
  // is then the same for everyone, so no hydration depends on who is asking.
  useEffect(() => {
    const ac = new AbortController();
    fetchIdentity(ac.signal).then((s) => {
      if (ac.signal.aborted) return;
      const prior = recall();
      setState(s);
      setAnswer({
        nickname: s?.nickname ?? prior.nickname,
        team: s?.team ?? prior.team,
        company: s?.company ?? prior.company,
      });
      setGateOpen(!!s?.needsName);
      // A failed lookup says nothing about the picture: keep the account's.
      if (s) setAvatarUrl(s.avatarUrl);
      setLoaded(true);
    });
    return () => ac.abort();
  }, []);

  const save = useCallback(async (nickname: string, company: string, team: number) => {
    /*
     * Signed in — write it to the ACCOUNT.
     *
     * `auth.update` PATCHes /api/auth/profile, which is the only place a
     * nickname is unique and a team belongs to a person; it also re-stores the
     * remembered credentials under the new nickname so the automatic sign-in
     * keeps working after a rename. The server mirrors both onto the visitor
     * document afterwards, so the admin console's older screens keep showing a
     * name rather than a generated one.
     *
     * The claim path below is what anonymous visitors used and is kept for
     * exactly them — nothing on the translator reaches it any more, but
     * /api/identity is still open and still answers.
     */
    // An empty company is not sent: the route 400s "sent-and-empty", and the
    // company is optional, so blank means "leave it as it is".
    if (auth.user) await auth.update({ nickname, team, ...(company ? { company } : {}) });
    else await claimIdentity(nickname, team, company);
    // Only after the server has confirmed the write. Remembering first and
    // failing second would leave the browser claiming an identity the database
    // never received.
    remember(nickname, team, company);
    setAnswer({ nickname, team, company });
    // Both of these are the point of sharing the state at all: the wall closes
    // and the gear stops waving on the same commit, off one answer.
    setGateOpen(false);
    setState((s) => (s ? { ...s, needsName: false, nickname, team, company } : s));
  }, [auth]);

  const value = useMemo<Identity>(() => ({
    loaded: loaded || !!auth.user,
    state,
    // The account first, in both. It is the only copy that is unique to a
    // person; `answer` is the older per-browser/per-address one and remains the
    // fallback for a visitor who is not signed in.
    nickname: auth.user?.nickname ?? answer.nickname,
    team: auth.user?.team ?? answer.team,
    /*
     * The account first, then whatever was claimed, then the default.
     *
     * The last step is what fills the box for somebody who has never been
     * asked — an anonymous visitor, or an account from before the field
     * existed reaching this through a stale lookup. Everybody here belongs to
     * the same company, so a filled box is a true suggestion rather than a
     * blank that has to be typed out; it is a default, and it is editable.
     */
    // No default any more (2026-10-06): the company is optional, and a
    // pre-filled `Shark` would be saved by the next unrelated profile edit.
    company: auth.user?.company || answer.company || '',
    teams: state?.teams?.length ? state.teams : FALLBACK.teams,
    maxNickname: state?.maxNickname ?? FALLBACK.maxNickname,
    maxCompany: state?.maxCompany ?? FALLBACK.maxCompany,
    defaultCompany: state?.defaultCompany || FALLBACK.defaultCompany,
    // Never for an ACCOUNT. The `Chorse` lock is the anonymous-claim rule; an
    // account keeps its own nickname on team 0 (the server's syncVisitor passes
    // it as authoritative), and since sign-up stopped asking for a team every
    // new account IS on team 0 — locking here would rename them on first save.
    noTeamNickname: auth.user ? null : (state?.noTeamNickname ?? null),
    avatarUrl: avatarUrl !== undefined ? avatarUrl : avatarSrc(auth.user?.avatarKey),
    setAvatarUrl,
    // The server's decision, not the local form's state: the browser's
    // remembered copy pre-fills the boxes but is not evidence that the database
    // has an answer, and a visitor whose record was cleared has a stale
    // localStorage entry and is nameless again as far as this product cares.
    unnamed: !auth.user && !!state && !state.unavailable && !(state.nickname ?? '').trim(),
    // Never for somebody signed in: their nickname is their account's, and the
    // wall exists only to collect one from people who have not given one.
    // Never for a guest either (2026-10-06): a signed-out visitor now uses the
    // translator on free credits, and the sign-up prompt (GuestPrompt) is how
    // they are asked who they are — not a nickname wall.
    gateOpen: false,
    save,
  }), [loaded, state, answer, gateOpen, save, avatarUrl, auth.user, auth.user?.avatarKey]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
